import { useEffect, useState, useCallback } from 'react'
import {
  RefreshCw, AlertTriangle, Shield, ChevronRight,
  Clock, ListCheck, X, ChevronDown, ChevronUp, User,
  Activity,
} from 'lucide-react'
import styles from './DrillTemplatesPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('dr_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface TemplateListItem {
  id: number
  name: string
  drill_type: string
  description: string
  estimated_duration_hours: number
  step_count: number
}

interface TemplateStep {
  id: number
  step_number: number
  title: string
  description: string
  expected_duration_minutes: number
  responsible_role: string
}

interface TemplateDetail {
  id: number
  name: string
  drill_type: string
  description: string
  estimated_duration_hours: number
  steps: TemplateStep[]
}

// ── Color maps ────────────────────────────────────────────────────────────────

const DRILL_TYPE_COLORS: Record<string, string> = {
  FULL_FAILOVER: '#1A7B7E',
  PARTIAL_FAILOVER: '#0891B2',
  TABLETOP: '#7C3AED',
  BACKUP_RESTORE: '#D97706',
  COMMUNICATION: '#EA580C',
}

// ── Helper ────────────────────────────────────────────────────────────────────

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span
      className={styles.badge}
      style={{ '--bc': color } as React.CSSProperties}
    >
      {label}
    </span>
  )
}

// ── Step panel ────────────────────────────────────────────────────────────────

function StepsPanel({
  templateId,
  onClose,
}: {
  templateId: number
  onClose: () => void
}) {
  const [detail, setDetail] = useState<TemplateDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedStep, setExpandedStep] = useState<number | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetch(`${DR_API}/api/v1/dr/drill-templates/${templateId}/`, {
      headers: authHeaders(),
    })
      .then(r => {
        if (!r.ok) throw new Error(`API error: ${r.status}`)
        return r.json()
      })
      .then(d => { setDetail(d); setLoading(false) })
      .catch(e => { setError(e.message); setLoading(false) })
  }, [templateId])

  return (
    <div className={styles.panelOverlay} onClick={onClose}>
      <div className={styles.panel} onClick={e => e.stopPropagation()}>
        <div className={styles.panelHeader}>
          <div className={styles.panelHeaderLeft}>
            <ListCheck size={16} />
            <h2 className={styles.panelTitle}>
              {loading ? 'Loading…' : (detail?.name ?? 'Template')}
            </h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {loading && (
          <div className={styles.loadingRow}>
            <RefreshCw size={16} className={styles.spin} />
            <span>Loading template…</span>
          </div>
        )}

        {error && (
          <div className={styles.errorBanner}>
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {detail && !loading && (
          <div className={styles.panelBody}>
            <div className={styles.panelMeta}>
              <Badge
                label={detail.drill_type.replace(/_/g, ' ')}
                color={DRILL_TYPE_COLORS[detail.drill_type] ?? '#1A7B7E'}
              />
              <span className={styles.panelDuration}>
                <Clock size={12} /> {detail.estimated_duration_hours}h estimated
              </span>
              <span className={styles.panelStepCount}>
                <Activity size={12} /> {detail.steps.length} steps
              </span>
            </div>

            {detail.description && (
              <p className={styles.panelDesc}>{detail.description}</p>
            )}

            <div className={styles.stepsLabel}>Steps</div>
            <div className={styles.stepsList}>
              {detail.steps.length === 0 && (
                <p className={styles.noSteps}>No steps defined for this template.</p>
              )}
              {detail.steps.map(step => {
                const expanded = expandedStep === step.id
                return (
                  <div key={step.id} className={styles.stepItem}>
                    <div
                      className={styles.stepHeader}
                      onClick={() => setExpandedStep(expanded ? null : step.id)}
                    >
                      <div className={styles.stepNum}>{step.step_number}</div>
                      <div className={styles.stepSummary}>
                        <span className={styles.stepTitle}>{step.title}</span>
                        <div className={styles.stepMeta}>
                          {step.expected_duration_minutes > 0 && (
                            <span className={styles.stepDuration}>
                              <Clock size={10} /> {step.expected_duration_minutes} min
                            </span>
                          )}
                          {step.responsible_role && (
                            <span className={styles.stepRole}>
                              <User size={10} /> {step.responsible_role}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles.stepToggle}>
                        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </div>
                    {expanded && step.description && (
                      <div className={styles.stepDesc}>{step.description}</div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Template card ─────────────────────────────────────────────────────────────

function TemplateCard({
  template,
  onViewSteps,
}: {
  template: TemplateListItem
  onViewSteps: (id: number) => void
}) {
  return (
    <div className={styles.templateCard}>
      <div className={styles.templateCardTop}>
        <div className={styles.templateCardIcon}>
          <ListCheck size={20} />
        </div>
        <div className={styles.templateCardBadges}>
          <Badge
            label={template.drill_type.replace(/_/g, ' ')}
            color={DRILL_TYPE_COLORS[template.drill_type] ?? '#1A7B7E'}
          />
        </div>
      </div>
      <h3 className={styles.templateName}>{template.name}</h3>
      {template.description && (
        <p className={styles.templateDesc}>{template.description}</p>
      )}
      <div className={styles.templateStats}>
        <div className={styles.templateStat}>
          <Clock size={12} />
          <span>{template.estimated_duration_hours}h</span>
          <span className={styles.templateStatLabel}>Duration</span>
        </div>
        <div className={styles.templateStat}>
          <Activity size={12} />
          <span>{template.step_count}</span>
          <span className={styles.templateStatLabel}>Steps</span>
        </div>
      </div>
      <button
        className={styles.viewStepsBtn}
        onClick={() => onViewSteps(template.id)}
      >
        View Steps
      </button>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DrillTemplatesPage() {
  const [templates, setTemplates] = useState<TemplateListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const fetchTemplates = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/drill-templates/`, {
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error(`API error: ${res.status}`)
      const data = await res.json()
      setTemplates(Array.isArray(data) ? data : (data.results ?? []))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load templates.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchTemplates() }, [fetchTemplates])

  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroBreadcrumb}>
            <Shield size={12} />
            <span>Aptean Platform</span>
            <ChevronRight size={11} />
            <span>Disaster Recovery</span>
            <ChevronRight size={11} />
            <span>Drill Templates</span>
          </div>
          <h1 className={styles.heroTitle}>DR Drill Templates</h1>
          <p className={styles.heroSub}>
            Reusable step-by-step templates for planning and executing disaster recovery drills.
          </p>
        </div>
        <div className={styles.heroRight}>
          <div className={styles.heroBadge}>
            <ListCheck size={22} />
            <div className={styles.heroBadgeNum}>{loading ? '—' : templates.length}</div>
            <div className={styles.heroBadgeSub}>Templates</div>
          </div>
          <button className={styles.btnSecondary} onClick={fetchTemplates} disabled={loading}>
            <RefreshCw size={13} className={loading ? styles.spin : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button className={styles.retryBtn} onClick={fetchTemplates}>Retry</button>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className={styles.loadingRow}>
          <RefreshCw size={18} className={styles.spin} />
          <span>Loading templates…</span>
        </div>
      )}

      {/* ── Grid ── */}
      {!loading && !error && templates.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><ListCheck size={36} /></div>
          <h3>No templates defined</h3>
          <p>DR drill templates haven't been created yet. Contact your DR administrator.</p>
        </div>
      )}

      {!loading && templates.length > 0 && (
        <div className={styles.templateGrid}>
          {templates.map(t => (
            <TemplateCard
              key={t.id}
              template={t}
              onViewSteps={id => setSelectedId(id)}
            />
          ))}
        </div>
      )}

      {/* ── Side panel ── */}
      {selectedId !== null && (
        <StepsPanel
          templateId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

    </div>
  )
}
